import React, { useState, useEffect } from "react";
import { db } from "../../../config/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";

// Simple admin editor for the Tech Prep Showcase confirmation email template.
// Only the top portion (above the summary table) is managed here. The API
// that sends the email will merge this content with the dynamic table
// that is generated from the submission data.

const COLLECTION_NAME = "ShowcaseEmailTemplate";
const DOC_ID = "main"; // Use a single doc with id 'main' for the template

const ShowcaseEmailTemplate = () => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showcaseDate, setShowcaseDate] = useState("");
  const [medicalDate, setMedicalDate] = useState("");
  const [virtualDate, setVirtualDate] = useState("");
  const [medBioUrl, setMedBioUrl] = useState("");
  const [youTubeUrl, setYouTubeUrl] = useState("");
  const [techPrepUrl, setTechPrepUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const ref = doc(db, COLLECTION_NAME, DOC_ID);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setSubject(data.subject || "Showcase Registration Summary");
          // load optional dates (stored as ISO strings)
          setShowcaseDate(data.showcaseDate || "");
          setMedicalDate(data.medicalDate || "");
          setVirtualDate(data.virtualDate || "");
          setMedBioUrl(data.medBioUrl || "");
          setYouTubeUrl(data.youTubeUrl || "");
          setTechPrepUrl(data.techPrepUrl || "");
        } else {
          setSubject("Showcase Registration Summary");
          setShowcaseDate("");
          setMedicalDate("");
          setVirtualDate("");
          setMedBioUrl("");
          setYouTubeUrl("");
          setTechPrepUrl("");
        }
      } catch (err) {
        console.error("Error loading showcase email template", err);
        setSnackbar({
          open: true,
          message: "Error loading template",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const ref = doc(db, COLLECTION_NAME, DOC_ID);
      await setDoc(
        ref,
        {
          subject,
          showcaseDate,
          medicalDate,
          virtualDate,
          medBioUrl,
          youTubeUrl,
          techPrepUrl,
        },
        { merge: true },
      );
      setSnackbar({
        open: true,
        message: "Template saved",
        severity: "success",
      });
    } catch (err) {
      console.error("Error saving showcase email template", err);
      setSnackbar({
        open: true,
        message: "Error saving template",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={6}>
        <TechPrepPortalMgtBreadCrumbs pageName="Showcase Email Template" />
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3} mt={6}>
      <TechPrepPortalMgtBreadCrumbs pageName="Showcase Email Template" />
      <Typography variant="h5" gutterBottom>
        Tech Prep Showcase Email Template
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        This content controls the top portion of the confirmation email sent
        when a Tech Prep Showcase application is submitted. The summary table
        with project details will be added automatically by the system.
      </Typography>

      <Paper sx={{ p: 2, mt: 2 }}>
        <TextField
          fullWidth
          label="Email Header"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          margin="normal"
        />
        {/* Date pickers using native date input to avoid extra libs */}
        <TextField
          fullWidth
          label="Showcase Date"
          type="date"
          value={showcaseDate}
          onChange={(e) => setShowcaseDate(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          label="Medical Bioscience Date"
          type="date"
          value={medicalDate}
          onChange={(e) => setMedicalDate(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          label="Virtual Team Date"
          type="date"
          value={virtualDate}
          onChange={(e) => setVirtualDate(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />

        <Typography
          variant="subtitle1"
          fontWeight={600}
          color="primary"
          sx={{ mt: 3, mb: 1 }}
        >
          Email Links
        </Typography>

        <Grid container spacing={2}>
          {/* Med Bioscience Paper URL */}
          <Grid item xs={12} md={4}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Med Bioscience Paper URL
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="https://..."
                value={medBioUrl}
                onChange={(e) => setMedBioUrl(e.target.value)}
              />
              <IconButton
                size="small"
                disabled={!medBioUrl}
                onClick={() =>
                  window.open(medBioUrl, "_blank", "noopener,noreferrer")
                }
                sx={{
                  color: medBioUrl ? "#1976d2" : "text.disabled",
                  flexShrink: 0,
                }}
                aria-label="Open Med Bioscience URL"
              >
                <FontAwesomeIcon icon={faLink} size="sm" />
              </IconButton>
            </Box>
          </Grid>

          {/* YouTube Presentation URL */}
          <Grid item xs={12} md={4}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              YouTube Presentation URL
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="https://..."
                value={youTubeUrl}
                onChange={(e) => setYouTubeUrl(e.target.value)}
              />
              <IconButton
                size="small"
                disabled={!youTubeUrl}
                onClick={() =>
                  window.open(youTubeUrl, "_blank", "noopener,noreferrer")
                }
                sx={{
                  color: youTubeUrl ? "#1976d2" : "text.disabled",
                  flexShrink: 0,
                }}
                aria-label="Open YouTube URL"
              >
                <FontAwesomeIcon icon={faLink} size="sm" />
              </IconButton>
            </Box>
          </Grid>

          {/* Tech Prep URL */}
          <Grid item xs={12} md={4}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Tech Prep URL
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="https://mvtechprep.org"
                value={techPrepUrl}
                onChange={(e) => setTechPrepUrl(e.target.value)}
              />
              <IconButton
                size="small"
                disabled={!techPrepUrl}
                onClick={() =>
                  window.open(techPrepUrl, "_blank", "noopener,noreferrer")
                }
                sx={{
                  color: techPrepUrl ? "#1976d2" : "text.disabled",
                  flexShrink: 0,
                }}
                aria-label="Open Tech Prep URL"
              >
                <FontAwesomeIcon icon={faLink} size="sm" />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Template"}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShowcaseEmailTemplate;
